import React, { useEffect, useMemo, useState } from 'react';

import { useMixedAssetAllocation } from '@/cip/api/assetAllocation';
import {
    ASSET_CLASS_BREAKDOWN_CATEGORIES,
    formatRatio,
    getAssetAllocationCategoryLabel,
} from 'utils/asset-allocation-utils';
import { getCategoryColor } from 'views/household/OverviewTab/constants';

import { computeMixedAssetAllocationWeights } from './computeMixedAssetAllocationWeights';
import { StrategyElementFormValue } from './model-form-schema';

interface ModelExposurePanelProps {
    elements: StrategyElementFormValue[];
}

const DEBOUNCE_MS = 400;

export const ModelExposurePanel: React.FC<ModelExposurePanelProps> = ({ elements }) => {
    const [debounced, setDebounced] = useState(elements);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(elements), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [elements]);

    const { strategyWeights, securityWeights } = useMemo(
        () => computeMixedAssetAllocationWeights(debounced),
        [debounced],
    );

    const { data: exposure = [], isLoading } = useMixedAssetAllocation(strategyWeights, securityWeights);

    const slices = ASSET_CLASS_BREAKDOWN_CATEGORIES.map((category) => ({
        category,
        weight: exposure.find((e) => e.category === category)?.weight ?? 0,
    }))
        .filter((slice) => slice.weight > 0)
        .sort((a, b) => b.weight - a.weight);

    return (
        <div className="border-cocoa-200 flex flex-col gap-4 rounded-lg border bg-white p-6">
            <div>
                <h4 className="text-cocoa-1000 text-lg font-medium">Asset Exposure</h4>
                <p className="text-cocoa-500 text-xs">Blended across constituent strategies</p>
            </div>

            {strategyWeights.length === 0 && securityWeights.length === 0 ? (
                <p className="text-cocoa-500 text-sm">Add strategies to see blended exposure.</p>
            ) : (
                <>
                    <div className="bg-cocoa-0 flex h-6 overflow-hidden rounded">
                        {slices.map((slice) => (
                            <div
                                key={slice.category}
                                style={{
                                    width: `${slice.weight * 100}%`,
                                    backgroundColor: getCategoryColor(slice.category),
                                }}
                            />
                        ))}
                    </div>

                    <div className="flex flex-col gap-2">
                        {slices.map((slice) => (
                            <div key={slice.category} className="flex items-center gap-2">
                                <span
                                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                                    style={{ backgroundColor: getCategoryColor(slice.category) }}
                                />
                                <span className="text-cocoa-700 text-sm">
                                    {getAssetAllocationCategoryLabel(slice.category)}
                                </span>
                                <span className="text-cocoa-1000 ml-auto shrink-0 text-sm font-medium tabular-nums">
                                    {formatRatio(slice.weight)}
                                </span>
                            </div>
                        ))}
                        {isLoading && slices.length === 0 && <p className="text-cocoa-500 text-sm">Calculating...</p>}
                    </div>
                </>
            )}
        </div>
    );
};
